/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUiDropdownMenu from "@radix-ui/react-dropdown-menu";
import React, { Fragment, useCallback } from "react";
import * as RPNInput from "react-phone-number-input";

import type { ThemeType } from "../../../../../global/emotion";
import {
  dropShadow,
  numericPixels,
  numericValues,
  radius,
  shades,
  spacing,
  useTheme,
} from "../../../../../global/emotion";
import { CheckmarkIcon } from "../../../_base/icons/Icon/GeneralIcons";
import { ChevronDownIcon } from "../../../_base/icons/Icon/NavigationIcons";
import { ScrollArea } from "../../other/ScrollArea/ScrollArea.emotion";
import { PhoneCountryFlag } from "./PhoneCountryFlag.emotion";

const buttonStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.neutralsLowest,
  border: `2px solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: `${radius.ms} 0 0 ${radius.ms}`,
  borderRight: "none",
  color: theme.semanticColors.neutralsVeryHigh,
  cursor: "pointer",
  display: "flex",
  height: "auto",
  justifyContent: "center",
  paddingLeft: spacing.ms,
  position: "relative",
  width: `${numericPixels[72]}`,
  ":hover:not(:disabled)": {
    backgroundColor: theme.semanticColors.neutralsVeryLow,
    borderColor: theme.semanticColors.neutralsVeryLow,
    zIndex: "1",
  },
  ":disabled": {
    cursor: "not-allowed",
    paddingLeft: spacing.none,
  },
});
const dropdownContentStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  border: `1px solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: radius.ms,
  boxShadow: `${numericValues[0]} ${dropShadow.s} ${dropShadow.ms} ${numericValues[0]} ${shades.shade10}`,
  marginTop: spacing.xxs,
});
const scrollStyle: Interpolation = {
  height: numericPixels[200],
};
const dropdownContentInnerStyle: Interpolation = {
  padding: spacing.xxs,
};
const dropdownItemStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderRadius: radius.s,
  cursor: "pointer",
  display: "flex",
  gap: spacing.s,
  padding: spacing.s,
  ":hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
  },
});
const dropdownItemSelectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  ":hover": {
    backgroundColor: theme.semanticColors.neutralsVeryLow,
  },
});
const dropdownItemTitleStyle: Interpolation = {
  flexGrow: 1,
};
const dropdownItemCodeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  padding: `0 ${spacing.xs}`,
});

type CountrySelectOption = { label: string; value: RPNInput.Country };

export type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
  cssOverride?: {
    button?: Interpolation;
    dropdown?: Interpolation;
  };
};

export const PhoneCountrySelect = ({
  disabled,
  value,
  onChange,
  options,
  cssOverride,
}: CountrySelectProps) => {
  const { themeContainerRef } = useTheme();

  // Setup lists of countries to display
  const preferredCodes = ["US", "MX", "BR", "PH", "PL"];
  const preferredCountries = preferredCodes
    .map((code) => options.find((x) => x.value === code))
    .filter(Boolean);
  const otherCountries = options.filter(
    (x) => !preferredCodes.includes(x.value),
  );

  // Callbacks
  const handleSelect = useCallback(
    (country: RPNInput.Country) => {
      onChange(country);
    },
    [onChange],
  );

  return (
    <RadixUiDropdownMenu.Root>
      <RadixUiDropdownMenu.Trigger disabled={disabled} asChild>
        <button disabled={disabled} css={[buttonStyle, cssOverride?.button]}>
          <PhoneCountryFlag country={value} countryName={value} />
          {!disabled && <ChevronDownIcon />}
        </button>
      </RadixUiDropdownMenu.Trigger>
      <RadixUiDropdownMenu.Portal container={themeContainerRef.current}>
        <Fragment>
          <RadixUiDropdownMenu.Content
            align="start"
            css={[dropdownContentStyle, cssOverride?.dropdown]}
          >
            <ScrollArea css={scrollStyle}>
              <div css={dropdownContentInnerStyle}>
                {[...preferredCountries, ...otherCountries]
                  .filter((x) => x.value)
                  .map((option) => (
                    <RadixUiDropdownMenu.Item
                      css={[
                        dropdownItemStyle,
                        option.value === value && dropdownItemSelectedStyle,
                      ]}
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                    >
                      <PhoneCountryFlag
                        country={option.value}
                        countryName={option.label}
                      />
                      <div css={dropdownItemTitleStyle}>
                        <span>{option.label}</span>
                        {option.value && (
                          <span css={dropdownItemCodeStyle}>
                            {`+${RPNInput.getCountryCallingCode(option.value)}`}
                          </span>
                        )}
                      </div>
                      {option.value === value && <CheckmarkIcon.SimpleOn />}
                    </RadixUiDropdownMenu.Item>
                  ))}
              </div>
            </ScrollArea>
          </RadixUiDropdownMenu.Content>
        </Fragment>
      </RadixUiDropdownMenu.Portal>
    </RadixUiDropdownMenu.Root>
  );
};
PhoneCountrySelect.displayName = "PhoneCountrySelect";
